import type { Expediente } from '../expedientes/cliente-expedientes.js'
import { ErrorApi } from '../identidad/cliente-identidad.js'
import { cerrarCarga, mostrarCarga, notificar } from '../fundacion/alertas.js'
import {
  agregarEvidencia,
  guardarInforme,
  guardarRae,
  obtenerProyecto,
  type DatosRae,
} from './cliente-proyecto.js'
const elemento = <T extends Element>(selector: string): T => {
  const dato = document.querySelector<T>(selector)
  if (!dato) throw new Error(`No existe ${selector}`)
  return dato
}
let expediente: Expediente | null = null
let proyecto: Awaited<ReturnType<typeof obtenerProyecto>>['datos'] | null = null
const campos: Array<keyof DatosRae> = [
  'nombreProyecto',
  'integrantes',
  'problema',
  'meta',
  'objetivos',
  'rastreoAntecedentes',
  'marcoTeorico',
  'metodologia',
  'aportes',
  'bibliografia',
]
const mensajes = (error: unknown) =>
  error instanceof ErrorApi ? error.message : 'No fue posible completar la operación.'
function aviso(texto: string, tipo = 'success') {
  const alerta = elemento<HTMLElement>('#alerta-proyecto')
  alerta.className = 'alert d-none'
  notificar(texto, tipo === 'danger' ? 'error' : (tipo as 'success' | 'warning' | 'info'))
}
function puedeEditar() {
  return Boolean(expediente && expediente.estado === 'ABIERTO')
}
export async function mostrarProyecto(nuevo: Expediente, roles: string[]): Promise<void> {
  expediente = nuevo
  elemento('#panel-proyecto').classList.remove('d-none')
  try {
    proyecto = (await obtenerProyecto(nuevo.id)).datos
  } catch {
    proyecto = null
  }
  const rae = elemento<HTMLFormElement>('#formulario-rae')
  const informe = elemento<HTMLFormElement>('#formulario-informe')
  const ejecucion = elemento<HTMLElement>('#contenedor-ejecucion-proyecto')
  const esEvaluacion = nuevo.etapa === 'EVALUACION'
  const esEjecucion = nuevo.etapa === 'EJECUCION'
  rae.classList.toggle('d-none', !(roles.includes('ESTUDIANTE') && (esEvaluacion || esEjecucion)))
  ejecucion.classList.toggle('d-none', !(roles.includes('ESTUDIANTE') && esEjecucion))
  if (proyecto) {
    for (const campo of campos) {
      const entrada = rae.elements.namedItem(campo) as HTMLInputElement | HTMLTextAreaElement | null
      if (entrada) entrada.value = String(proyecto[campo] ?? '')
    }
    const texto = informe.elements.namedItem('informeResultado') as HTMLTextAreaElement | null
    if (texto) texto.value = proyecto.informeResultado ?? ''
    elemento<HTMLInputElement>('#version-proyecto').value = String(proyecto.version)
  }
  rae
    .querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
      'input,textarea,select',
    )
    .forEach((control) => {
      control.disabled = !esEvaluacion || !puedeEditar()
    })
  elemento('#panel-proyecto').scrollIntoView({ behavior: 'smooth', block: 'start' })
}
elemento<HTMLFormElement>('#formulario-rae').addEventListener('submit', (evento) => {
  evento.preventDefault()
  const formulario = evento.currentTarget as HTMLFormElement
  if (!formulario.checkValidity() || !expediente || expediente.etapa !== 'EVALUACION') return
  const datos = Object.fromEntries(
    campos.map((campo) => [
      campo,
      (formulario.elements.namedItem(campo) as HTMLInputElement | HTMLTextAreaElement).value,
    ]),
  ) as unknown as DatosRae
  mostrarCarga('Guardando el proyecto educativo…')
  void guardarRae(expediente.id, datos)
    .then(() => aviso('RAE guardado y auditado.'))
    .catch((error: unknown) => aviso(mensajes(error), 'danger'))
    .finally(cerrarCarga)
})
elemento<HTMLFormElement>('#formulario-informe').addEventListener('submit', (evento) => {
  evento.preventDefault()
  if (!expediente) return
  const formulario = evento.currentTarget as HTMLFormElement
  const texto = (formulario.elements.namedItem('informeResultado') as HTMLTextAreaElement).value
  const version = Number(elemento<HTMLInputElement>('#version-proyecto').value)
  mostrarCarga('Guardando el informe de resultados…')
  void guardarInforme(expediente.id, texto, version)
    .then(() => aviso('Informe guardado y auditado.'))
    .catch((error: unknown) => aviso(mensajes(error), 'danger'))
    .finally(cerrarCarga)
})
elemento<HTMLInputElement>('#evidencia-proyecto').addEventListener('change', (evento) => {
  if (!expediente) return
  const archivo = (evento.currentTarget as HTMLInputElement).files?.[0]
  if (archivo && expediente.etapa === 'EJECUCION') {
    mostrarCarga('Verificando y subiendo la evidencia…')
    void agregarEvidencia(expediente.id, archivo)
      .then(() => aviso('Evidencia almacenada.'))
      .catch((error: unknown) => aviso(mensajes(error), 'danger'))
      .finally(cerrarCarga)
  }
})
